/* eslint-disable @reatom/unit-naming-rule */
import { useCtx } from '@reatom/npm-react'
import { useEffect } from 'react'
import styled from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { reatomExploreTableCooking } from '@root/features/explore/atoms/explore-table/reatom-explore-table-cooking'
import { reatomExploreTableNew } from '@root/features/explore/atoms/explore-table/reatom-explore-table-new'
import { reatomExploreTableTrending } from '@root/features/explore/atoms/explore-table/reatom-explore-table-trending'
import { ExploreFeedType } from '@root/features/explore/model/explore-feed-type'
import { ExploreTable } from '@root/features/explore/ui/explore-table/explore-table'
import { useExploreTableTab } from '@root/features/explore/ui/hooks/use-explore-tab'
import { TOPBAR_HEIGHT } from '@root/shared/ui-kit/utils/page-layout'

import { ExploreTableHeader } from './explore-table-header'

type Props = {}

export const ExploreTableSection: React.FC<Props> = () => {
  const [tableTab] = useExploreTableTab()
  const trendingTableModel = useMemo(() => reatomExploreTableTrending(), [])
  const cookingTableModel = useMemo(() => reatomExploreTableCooking(), [])
  const newTableModel = useMemo(() => reatomExploreTableNew(), [])
  const ctx = useCtx()

  const model = useMemo(() => {
    return match(tableTab)
      .with(ExploreFeedType.Trending, () => trendingTableModel)
      .with(ExploreFeedType.Cooking, () => cookingTableModel)
      .with(ExploreFeedType.New, () => newTableModel)
      .exhaustive()
  }, [cookingTableModel, newTableModel, tableTab, trendingTableModel])

  /**
   * Only the tab on screen is subscribed.
   *
   * All three used to be, and each `initialize` is not cheap: it opens the
   * feed's socket subscription, a per-pair info stream and a quick-buy quote
   * stream for every row, and the filter and period reactions that refetch
   * the list. Measured on production 2026-10-02, with the table open and two
   * of the three tabs invisible, `get-trending` went out **twice every thirty
   * seconds** — once from this model and once from the explore column — at
   * 73.5 KiB compressed a time, for lists nobody was looking at.
   *
   * The cost of switching back is one read of a list the server already has
   * cached: it answers a warm feed in about thirty milliseconds, so the wait
   * is the round trip, and the model keeps its last rows to show meanwhile.
   */
  useEffect(() => model.initialize(ctx), [ctx, model])

  return (
    <TableContainer>
      <ExploreTableHeader tableModel={model} />
      <TableContent>
        <ExploreTable key={tableTab} tableModel={model} feedType={tableTab} />
      </TableContent>
    </TableContainer>
  )
}

const TableContainer = styled.div`
  /* Keeps the sticky header's z-index from competing with the page's drawer */
  isolation: isolate;

  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: calc(100cqh - ${TOPBAR_HEIGHT}px);
  scrollbar-color: auto;
`

const TableContent = styled.div`
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
  // FOOTER HEIGHT
  padding-bottom: 24px;
`
